import themeGet from "@styled-system/theme-get";
import { Box, Button, Flex, Icon, Small, TextInput } from "orcs-design-system";
import { NavLink } from "react-router-dom";
import styled, { css } from "styled-components";
import moment from "moment";
import { resolveAiDashboardPath } from "src/util/aiDashboardPath";
import { sidebarRowLayout } from "../shared/sidebarRowLayout";
import type { Dashboard } from "./types";

/** Takes the row's spare width and ellipsises rather than wrapping. */
const rowTitle = css`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DashboardLink = styled(NavLink)`
  ${rowTitle}
  text-decoration: none !important;
  font-size: ${themeGet("fontSizes.1")};
  color: ${themeGet("colors.bodyText")};

  &.active,
  &:hover {
    color: ${themeGet("colors.bodyText")};
  }
`;

/** Muted stand-in for the link when a v1 dashboard has no chat to link to. */
const OrphanTitle = styled(Box)`
  ${rowTitle}
  color: ${themeGet("colors.mutedText")};
`;

/** nowrap so the age can't fold onto two lines as the row makes room for it. */
const RowAge = styled(Small)`
  font-size: 0.75rem;
  color: ${themeGet("colors.mutedText")};
  white-space: nowrap;
`;

/** Row wrapper so the action buttons sit beside the link rather than inside it. */
const DashboardRow = styled(Flex)`
  ${sidebarRowLayout}

  &:has(a.active) {
    background-color: ${themeGet("colors.navActiveBackground")};
  }

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
  }

  /* Panel rows are narrow, so the age and the actions collapse to nothing until
     the row is hovered and the title gets the whole row. Collapsing the width
     rather than fading it out is what actually hands the space back — an
     opacity-only hide still reserves it. They stay in flow and focusable, so
     focus-within reveals them for keyboard users too. */
  [data-dashboard-row-actions] {
    width: 0;
    overflow: hidden;
  }

  &:hover [data-dashboard-row-actions],
  &:focus-within [data-dashboard-row-actions] {
    width: auto;
  }
`;

// The action buttons are siblings of the row's NavLink rather than children of
// it, so a click on one cannot reach the link and no preventDefault is needed
// to stop the row navigating. The tests assert the location stays put.

type DashboardListItemProps = {
  dashboard: Dashboard;
  enableAIDataDrivenDashboards: boolean | undefined;
  isEditing: boolean;
  draftTitle: string;
  onDraftTitleChange: (title: string) => void;
  onStartRename: (dashboard: Dashboard) => void;
  onSaveRename: (dashboard: Dashboard) => void;
  onCancelRename: () => void;
  onDelete: (dashboard: Dashboard) => void;
};

const DashboardListItem = ({
  dashboard,
  enableAIDataDrivenDashboards,
  isEditing,
  draftTitle,
  onDraftTitleChange,
  onStartRename,
  onSaveRename,
  onCancelRename,
  onDelete,
}: DashboardListItemProps) => {
  const { id, title, aiChatId, createdAt, format } = dashboard;

  if (isEditing) {
    return (
      <DashboardRow key={id}>
        <TextInput
          autoFocus
          fullWidth
          height="28px"
          id={`ai-dashboard-title-${id}`}
          ariaLabel="Dashboard name"
          value={draftTitle}
          data-testid={`ai-dashboard-title-input-${id}`}
          onChange={(event) => onDraftTitleChange(event.target.value)}
          onBlur={() => onSaveRename(dashboard)}
          onKeyDown={(event) => {
            // Let the blur handler own the save so Enter can't
            // submit the rename twice.
            if (event.key === "Enter") {
              event.target.blur();
            }
            if (event.key === "Escape") {
              onCancelRename();
            }
          }}
        />
      </DashboardRow>
    );
  }

  const dashboardLink = resolveAiDashboardPath({
    dashboardId: id,
    chatId: aiChatId,
    format,
    enableAIDataDrivenDashboards,
  });

  return (
    <DashboardRow key={id}>
      {dashboardLink ? (
        <DashboardLink to={dashboardLink} title={title}>
          {title}
        </DashboardLink>
      ) : (
        // v1 without aiChatId — no legacy route; don't send users to v2.
        <OrphanTitle title="Dashboard is missing its chat link">
          {title}
        </OrphanTitle>
      )}

      <Flex
        alignItems="center"
        gap="xs"
        flex="0 0 auto"
        data-dashboard-row-actions
      >
        <RowAge>{moment(createdAt).fromNow()}</RowAge>
        <Button
          type="button"
          variant="ghost"
          small
          iconOnly
          ariaLabel={`Rename dashboard ${title}`}
          onClick={() => onStartRename(dashboard)}
          data-testid={`ai-dashboard-rename-${id}`}
        >
          <Icon icon={["fas", "pencil"]} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          small
          iconOnly
          ariaLabel={`Delete dashboard ${title}`}
          onClick={() => onDelete(dashboard)}
          data-testid={`ai-dashboard-delete-${id}`}
        >
          <Icon icon={["far", "trash-alt"]} />
        </Button>
      </Flex>
    </DashboardRow>
  );
};

export default DashboardListItem;
